import React from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { Category, EventPageType } from '@graphql/generated-base';
import type { BoxProps } from '@withjoy/joykit';
import { Box, ColumnsV2, Flex, NoSsr, TextV2 } from '@withjoy/joykit';
import type { LayoutBrannanProps } from '../../LayoutBrannan';
import type { PreparedPages } from './Body.constants';
import { pageWidgets } from './Body.constants';
import { shouldRenderGraphicAccent } from '@shared/utils/graphicAccent';
import { PrettyLink } from '@apps/guest/packages/layout-engine/components/PrettyLink';
import GraphicAccent from '@apps/guest/packages/layout-engine/components/GraphicAccent';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { EditFontData } from '@shared/utils/previewMessageBus';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display3ToPageTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { SCREENS } from '@shared/types/fontPackScreens';
import { AccommodationsCountdownExperiment } from '@apps/guest/components/AccommodationsCountdownExperiment';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { BrannanFrameMaxWidth } from '../Frames';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveText } from '@apps/guest/packages/layout-engine/components/ResponsiveText';
import { LazyRichText } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichText.lazy';
import { isRichTextContent } from '@shared/utils/html';
import { isLegacyOrMigratedStoryPage, isRegistryPage } from '@shared/utils/migratedCustomPages';
import { getPageHorizontalPadding, usePageDimensions } from '../../usePageDimensions';
import { useGuestPageContentRedesign } from '@apps/guest/hooks/useGuestPageContentRedesign';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
import { animationTransitionExt } from '@shared/utils/animationTransition';
import { useResolvedAccommodationsLayoutType } from '@apps/guest/routes/GuestSite/useResolvedAccommodationsLayoutType';

const FADE_IN_TRANSITION = animationTransitionExt({ property: 'opacity' });

// Wrapper math for full-viewport accommodations cases: 16px gap on each side of the viewport.
// The buffer (vs. a tighter 4px) clears the admin-preview iframe's vertical scrollbar and the
// overflow-x: clip ancestors in FrameOuter / ScrollableContainer, which were truncating the
// SVG outline's left/right strokes when it landed flush with the viewport edge.
const FULL_VIEWPORT_WRAPPER_CSS = { width: 'calc(100vw - 32px)', maxWidth: 'unset', left: 'calc(-50vw + 50% + 16px)' };

export interface BodyProps
  extends Readonly<
    {
      page: Maybe<EventPageFragment>;
      photo?: React.ReactNode;
      isWelcomePage: boolean;
      isCustomWelcomePage: boolean;
      /**
       * Honeymoon-redesign URL-deep-linked page bypasses <NoSsr> so the PageWidget renders SSR
       * (PRNT-4234). Decided by the layout so this component does not subscribe to the router:
       * every Body in a stacked layout would otherwise re-render on each scroll-driven URL replace.
       */
      isStaysRedesignCurrentPage?: boolean;
      /**
       * Whether this section's chrome has been revealed. Resolved by the layout rather than read
       * from PageContentVisibility here: a context read bypasses this component's `React.memo`, so
       * every reveal change re-rendered all nine sections even though at most one section's answer
       * had changed. Defaults to the context's own out-of-provider default.
       */
      isRevealed?: boolean;
    } & LayoutBrannanProps &
      BoxProps
  > {}

const Body: React.FC<BodyProps> = React.memo(
  ({ isSinglePage, page, event, eventHandle, photo, isWelcomePage, isCustomWelcomePage, isStaysRedesignCurrentPage = false, isRevealed = true, ...restProps }) => {
    /**
     * Determines whether the current page is the "accommodations" page.
     *
     * Previously, this logic relied on detecting the active route to adjust spacing and alignment
     * specifically for the accommodations page. However, with the new "Simple Scroll" layout —
     * where all pages are rendered at once — this approach is no longer sufficient.
     *
     * In the new layout, spacing and alignment should be applied consistently, regardless of routing.
     * Therefore, `isAccommodations` is now determined using `page.pageSlug`,
     * which uniquely identifies the accommodations page for new "Simple Scroll" layout.
     */
    const { isAccommodationsPage, isNativeAccommodationPage } = useAccommodationPageChecks(page?.pageSlug);

    const { enabled: isGuestPageContentRedesignEnabled } = useGuestPageContentRedesign();

    const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } }, false);

    const { isFullWidth: isFullWidthPage, maxWidth: pageMaxWidth, horizontalPadding } = usePageDimensions(page?.type, page?.pageSlug);
    const handleEditTextClick = useEventCallback((textContent: string, label: string, pageId: string) => {
      // send the message to the parent window to open the text editor
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editTextArea',
          inlineEditData: { textContent, label, pageId, isPageContainer: page?.isPageContainer || false, validationConstraints: { maxLength: 140 } },
        },
      });
    });

    const onInlineEditClick = useEventCallback(() => {
      if (page) {
        sendMessageToParentWindow({
          action: 'inlineEditingInteraction',
          source: 'joyPreview',
          value: {
            action: 'editPageNote',
            inlineEditData: {
              pageId: page.id,
              isPageContainer: page?.isPageContainer || false,
              pageSubTitle: page?.subTitle || '',
              pageType: page.type,
              pageSlug: page.pageSlug,
            },
          },
        });
      }
    });

    const { pageColor, artBackgroundColor, brannanInnerFrameBorder, themeId, isCustomArtApplied } = useColorPaletteProvider();
    // Pass eventId only on the accommodations page so the hook skips its query for unrelated routes.
    const { layoutType: accommodationsLayoutType } = useResolvedAccommodationsLayoutType(isAccommodationsPage ? event.id : undefined);
    // Brannan-blank-full conditions reused by the classic-default-full-width path AND the per-layout
    // full-viewport handling below (default + full_width_photo single-page bleed).
    const isBrannanFullWidth = pageColor === artBackgroundColor && !brannanInnerFrameBorder && themeId === 'blank' && !isCustomArtApplied;
    // Classic full-width accommodations: blank theme, no custom art, no inner frame border,
    // and page color matches art background — the content stretches edge-to-edge,
    // so the inline editor wrapper must also go full viewport width.
    // Scoped to the legacy `default` layout — the new alternate layouts (flashlight, full_width_photo,
    // etc.) own their bleed via `getPhotoBleedStyle`, and the 100vw wrapper shifts their centered
    // photo to the left edge of the admin preview iframe.
    const isClassicFullWidthAccommodationsPage = Boolean(isAccommodationsPage && !isSinglePage && accommodationsLayoutType === 'default' && isBrannanFullWidth);
    // True when the accommodations widget escapes to ~100vw (default and full_width_photo on
    // blank-full Brannan). Used to (a) widen the wrapper to viewport and (b) zero out the SVG
    // outline inset so the visible border lands exactly at the wrapper's 4px viewport-edge gap.
    const isAccommodationsFullViewport = Boolean(
      isAccommodationsPage && isBrannanFullWidth && (accommodationsLayoutType === 'default' || accommodationsLayoutType === 'full_width_photo'),
    );
    // Per-layout wrapper sizing for the accommodations page so the inline-editor outline hugs
    // each layout's actual content width (the page-content column is otherwise full width and
    // the outline lands much wider than what the layout renders). +24px accounts for the
    // accommodations page's 12px-each-side gutter so the inner content lands at its natural cap.
    // `default` is left to the existing classic / non-classic handling above.
    const accommodationsLayoutWrapperCSS = React.useMemo(() => {
      if (!isAccommodationsPage || isClassicFullWidthAccommodationsPage) return undefined;
      const PAGE_GUTTER = 24;
      switch (accommodationsLayoutType) {
        case 'stylish_hover':
          return { width: '100%', maxWidth: pxToRem(1100 + PAGE_GUTTER), marginX: 'auto' };
        case 'typographic':
        case 'side_by_side_simple':
        case 'side_by_side_elegant':
        case 'flashlight':
          // All four ride the brown-frame width (1024 + 24px gutter) so their outlines render
          // identically in frame-capped Brannan. The inner content keeps its natural cap
          // (860/900/590) — the wrapper just gives each layout a consistent outline boundary.
          return { width: '100%', maxWidth: pxToRem(BrannanFrameMaxWidth + PAGE_GUTTER), marginX: 'auto' };
        case 'full_width_photo':
          // Photo bleeds to viewport on blank-full Brannan, otherwise to the brown-frame edge.
          return isBrannanFullWidth ? FULL_VIEWPORT_WRAPPER_CSS : { width: '100%', maxWidth: pxToRem(BrannanFrameMaxWidth + PAGE_GUTTER), marginX: 'auto' };
        case 'default':
          // AccommodationsExpandedLayout escapes to 100vw whenever its `isFullWidth` is true
          // (same color/theme conditions). The outer `isClassicFullWidthAccommodationsPage`
          // path catches the multi-page case; this catches single-page where that path is
          // skipped — without this, the outline lands at column width while the content
          // already went 100vw.
          return isBrannanFullWidth ? FULL_VIEWPORT_WRAPPER_CSS : undefined;
        default:
          return undefined;
      }
    }, [accommodationsLayoutType, isAccommodationsPage, isClassicFullWidthAccommodationsPage, isBrannanFullWidth]);

    const onPageEditClick = useEventCallback(() => {
      if (page) {
        sendMessageToParentWindow({
          action: 'inlineEditingInteraction',
          source: 'joyPreview',
          value: {
            action: 'editPage',
            inlineEditData: {
              eventHandle,
              pageSlug: page.pageSlug,
              pageType: page.type,
              pageTitle: page.pageTitle,
              // CORE-6470: lets the editor route a userGenerated migrated squatter (pageKind === userCustom)
              // to the generic custom editor.
              pageKind: page.pageKind,
            },
          },
        });
      }
    });

    const handleEditFontClick = useEventCallback((inlineEditData: EditFontData) => {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: { action: 'editFont', inlineEditData },
      });
    });

    if (!page) {
      return null;
    }
    const { type, pageTitle, subTitle, pageSlug, id } = page;
    const graphicAccent = event.eventDesign?.accent;

    const PageWidget = pageWidgets[type as PreparedPages];
    const isSchedulePage = type === EventPageType.schedule;
    const isCardsSchedulePage = isSchedulePage && page?.layoutType === 'cards';
    // Legacy story only: CoupleStory owns the title, so Body hides title chrome (migrated custom keeps it).
    const isLegacyStoryPage = type === EventPageType.story;
    // Legacy or system-migrated story: suppress stale page note so it never stacks with text_component.
    const shouldSuppressStoryPageNote = isLegacyOrMigratedStoryPage(type, pageSlug, page.pageKind);
    const isRegistryPage = type === EventPageType.registry;
    const isTravelPage = type === EventPageType.travel;
    const isCustomPage = type === EventPageType.custom;
    const isAppPage = type === EventPageType.app;

    // Fade chrome (title + PageNote subtitle) in once this page has been visited.
    // On stacked single-page layouts this means only the deep-linked target
    // reveals at mount; as the user scrolls, visited pages accumulate.
    const chromeStyle: React.CSSProperties = { opacity: isRevealed ? 1 : 0, transition: FADE_IN_TRANSITION };

    const pageContentColumnStyle: React.CSSProperties = {
      paddingLeft: 0,
      paddingRight: 0,
      ...(isTravelPage && { display: 'flex', justifyContent: 'center' }),
      // Added a maxWidth limit to single page accommodations to prevent the travel map from taking up the full screen width when a user sets a full width page photo. Subtract horizontal padding from max width for proper edge spacing
      ...(isAccommodationsPage && isSinglePage && { maxWidth: pxToRem(BrannanFrameMaxWidth - getPageHorizontalPadding('accommodations').DESKTOP * 2) }),
    };

    const pageContentMarkup = PageWidget && (
      <ColumnsV2.Column
        id={pageSlug}
        offset={[0, 0]}
        style={pageContentColumnStyle}
        span={!isGuestPageContentRedesignEnabled && isSchedulePage ? [12, 10, isCardsSchedulePage ? 11 : 8] : [12, 12, 12]}
      >
        <Box
          width="100%"
          maxWidth={pageMaxWidth}
          marginX={isFullWidthPage ? undefined : 'auto'}
        >
          {/* Welcome page + honeymoon-redesign current page render SSR; other stacked pages NoSsr-wrapped (PR #14490, PRNT-4234).
            JSX inlined to avoid TS2774 (pageWidgets is total-typed). */}
          {isWelcomePage || isStaysRedesignCurrentPage ? (
            <PageWidget
              isSinglePage={isSinglePage}
              isAccommodations={isAccommodationsPage}
              isWelcomePage={isWelcomePage}
              event={event}
              eventHandle={eventHandle}
              pageId={id}
              pageSlug={pageSlug}
              pageTitle={pageTitle}
              subTitle={subTitle}
            />
          ) : (
            <NoSsr>
              <PageWidget
                isSinglePage={isSinglePage}
                isAccommodations={isAccommodationsPage}
                isWelcomePage={isWelcomePage}
                event={event}
                eventHandle={eventHandle}
                pageId={id}
                pageSlug={pageSlug}
                pageTitle={pageTitle}
                subTitle={subTitle}
              />
            </NoSsr>
          )}
        </Box>
      </ColumnsV2.Column>
    );

    const shouldShowGraphicAccent = shouldRenderGraphicAccent(graphicAccent);
    const graphicAccentMarkup = shouldShowGraphicAccent && (
      <GraphicAccent
        id="graphic-accent"
        data-testid={'body-accent'}
        accentId={graphicAccent!}
        isInlineEditable={true}
        pageName={page.type}
        pageSlug={page.pageSlug}
      />
    );

    const getGraphicAccentMarginTop = () => {
      if (page.photoV2 && !isWelcomePage) return 9;
      if ((isWelcomePage || isAppPage) && shouldShowGraphicAccent && !isSinglePage) return { _: 9, sm2: 0 };
      if (isCustomPage && !isWelcomePage) return { _: 9, sm2: 0 };
      return 0;
    };

    const getGraphicAccentMarginBottom = () => {
      if (shouldShowGraphicAccent && isAccommodationsPage) return { _: 3, sm2: 5 };
      if (shouldShowGraphicAccent) return { _: 6, sm2: 8 };
      if (!isSinglePage && isWelcomePage) return 10;
      return 0;
    };

    return (
      <ColumnsV2 {...restProps}>
        <>
          <Box
            width="100%"
            style={chromeStyle}
          >
            {photo}
          </Box>
          <ColumnsV2.Column
            style={isCustomPage ? { display: isNativeAccommodationPage && !subTitle ? 'none' : 'block' } : {}}
            span={isAccommodationsPage ? [12, 12] : [12, 10]}
            offset={isAccommodationsPage ? [0, 0] : [0, 1]}
          >
            <Flex
              flexDirection={'column'}
              alignItems={'center'}
              marginTop={getGraphicAccentMarginTop()}
              marginBottom={getGraphicAccentMarginBottom()}
              marginX={isFullWidthPage ? 0 : 'auto'}
              maxWidth={pageMaxWidth}
              paddingX={horizontalPadding}
              style={chromeStyle}
            >
              {graphicAccentMarkup}
            </Flex>
          </ColumnsV2.Column>
          <InlineEditor
            actionData={{
              edit: onPageEditClick,
              font: () => handleEditFontClick({ screen: SCREENS.categoryList }),
            }}
            inset={
              isMobile
                ? { x: 8, y: -16 }
                : // Full-viewport accommodations cases need x=0 so the visible outline lands at the
                  // wrapper's viewport-edge gap (16px, defined in FULL_VIEWPORT_WRAPPER_CSS); a
                  // positive x would compound the inset and push the outline further inward.
                  isAccommodationsFullViewport
                  ? { x: 0, y: -16 }
                  : // The single-page accommodations branch used `x: 20` for the legacy column-width
                    // wrapper. With per-layout wrappers (above) the wrapper already hugs content, so
                    // a larger inset over-shrinks the outline below the wrapper width.
                    isAccommodationsPage && isSinglePage && !accommodationsLayoutWrapperCSS
                    ? { x: 20, y: -16 }
                    : { x: 4, y: -16 }
            }
            wrapperType="actionOutside"
            componentName="page"
            elementLabel={page.pageTitle}
            pageName={page.type}
            pageSlug={page.pageSlug}
            stickyOnScroll={true}
            // This is the inline editing eligibility check for the page title. The sub title is handled as page note.
            isEligibleForInlineEditing={(!isWelcomePage || isCustomWelcomePage) && !isAppPage && !isLegacyStoryPage}
            wrapperCSS={
              // Full-viewport cases sit 4px from each viewport edge (combined with inset.x=0 above
              // so the visible outline lands exactly at the wrapper edge).
              isClassicFullWidthAccommodationsPage ? FULL_VIEWPORT_WRAPPER_CSS : (accommodationsLayoutWrapperCSS ?? { width: '100%' })
            }
          >
            <ColumnsV2.Column
              style={isCustomPage ? { display: isNativeAccommodationPage && !subTitle ? 'none' : 'block', zIndex: 1 } : { zIndex: 1 }}
              span={[12, 12]}
              offset={[0, 0]}
            >
              <Box
                width="100%"
                paddingX={horizontalPadding}
                paddingTop={isAppPage && !isSinglePage ? { _: 10, sm2: 0 } : 0}
              >
                <Flex
                  flexDirection={'column'}
                  alignItems={'center'}
                  width="100%"
                  marginBottom={isLegacyStoryPage || isRegistryPage || isWelcomePage ? 0 : isAccommodationsPage ? { _: 3, sm2: 5 } : { _: 7, sm2: 9 }}
                  marginX={isFullWidthPage ? 0 : 'auto'}
                  maxWidth={pageMaxWidth}
                >
                  {isWelcomePage && pageContentMarkup}
                  {!isWelcomePage && !isLegacyStoryPage && !isNativeAccommodationPage && (
                    <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
                      <Box style={chromeStyle}>
                        <ResponsiveText
                          data-testid={'body-pagetitle'}
                          typographyVariant="display3"
                          defaultFontSize={36}
                          maxWidth="calc(100vw - 24px)"
                          textAlign="center"
                          wordBreak="break-word"
                          text={pageTitle}
                          skipResize={false}
                          fontCategory={Category.PAGE_TITLE}
                        />
                      </Box>
                    </GuestSiteTypographyOverride>
                  )}

                  <Box style={chromeStyle}>
                    <PageNote
                      shouldSuppressStoryPageNote={shouldSuppressStoryPageNote}
                      isWelcomePage={isWelcomePage}
                      isCustomWelcomePage={isCustomWelcomePage}
                      subTitle={subTitle}
                      handleEditFontClick={handleEditFontClick}
                      handleEditTextClick={handleEditTextClick}
                      onInlineEditClick={onInlineEditClick}
                      id={id}
                      page={page}
                      type={type}
                      pageSlug={pageSlug}
                      isAccommodations={isAccommodationsPage}
                      isEligibleForInlineEditing={!isAppPage}
                    />
                  </Box>
                </Flex>
              </Box>
            </ColumnsV2.Column>

            {isAccommodationsPage ? <AccommodationsCountdownExperiment /> : null}

            {!isWelcomePage && (
              <Box
                width="100%"
                paddingX={horizontalPadding}
                display={'flex'}
                justifyContent={'center'}
              >
                {pageContentMarkup}
              </Box>
            )}
          </InlineEditor>
        </>
      </ColumnsV2>
    );
  },
);

const PageNote = ({
  shouldSuppressStoryPageNote,
  isWelcomePage,
  isCustomWelcomePage,
  subTitle,
  handleEditFontClick,
  handleEditTextClick,
  onInlineEditClick,
  id,
  page,
  type,
  pageSlug,
  isAccommodations,
  isEligibleForInlineEditing,
}: {
  shouldSuppressStoryPageNote: boolean;
  isWelcomePage: boolean;
  isCustomWelcomePage: boolean;
  subTitle: string | null | undefined;
  handleEditFontClick: (inlineEditData: EditFontData) => void;
  handleEditTextClick: (textContent: string, label: string, pageId: string) => void;
  onInlineEditClick: () => void;
  id: string;
  page: Maybe<EventPageFragment>;
  type: EventPageType;
  pageSlug: string;
  isAccommodations: boolean;
  isEligibleForInlineEditing: boolean;
}) => {
  const { tq } = useTranslation('guestKit');

  // No page note or sub title shown for story pages. Legacy story keeps its content in the
  // `CoupleStory` widget; the migrated custom story page keeps it in `text_component` — in both
  // cases the legacy `page_container.note` is a stale duplicate, so it never renders here (even
  // when the custom body is empty).
  if (shouldSuppressStoryPageNote) return null;

  // On custom welcome pages the GreetingMessageGuest JSON block handles the greeting;
  // rendering the page subtitle here as well would duplicate it.
  if (isCustomWelcomePage) return null;

  if (isWelcomePage && subTitle) {
    return (
      <Box marginTop={{ _: 6, sm2: 9 }}>
        <InlineEditor
          elementLabel={tq`Greetings`}
          labelPosition="bottom"
          actionData={{
            edit: () => handleEditTextClick(subTitle, tq`Greetings`, id),
            font: () => handleEditFontClick({ category: Category.SUB_HEADING }),
          }}
          wrapperCSS={{
            zIndex: 'banner',
          }}
          componentName="greeting"
          pageName={type}
          pageSlug={pageSlug}
          isEligibleForInlineEditing={isEligibleForInlineEditing}
        >
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2
              data-testid="body-subtitle"
              tagName="div"
              typographyVariant={'body4'}
              maxWidth={500}
              textAlign="center"
            >
              <PrettyLink source={page?.pageSlug}>{subTitle}</PrettyLink>
            </TextV2>
          </GuestSiteTypographyOverride>
        </InlineEditor>
      </Box>
    );
  }

  if (isAccommodations) {
    // Accommodations renders the page note as plain markup (no InlineEditor).
    // Edits flow through the admin form's "Note to Guests" editor.
    if (!subTitle) return null;
    // Plain fallback preserves existing behavior for legacy plain-text notes.
    const plainSubTitle = (
      <PrettyLink
        source={page?.pageSlug}
        allowedTypes={['EMAIL']}
      >
        {subTitle}
      </PrettyLink>
    );
    // Cap the note inside the viewport for long translations (e.g. Italian). 48px = 2 × paddingX={6};
    // boxSizing: border-box keeps padding inside maxWidth so the cap remains accurate.
    return (
      <Box
        width="100%"
        maxWidth="min(500px, calc(100vw - 48px))"
        marginX="auto"
        marginTop={{ _: 7, sm2: 9 }}
        paddingX={6}
        boxSizing="border-box"
      >
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            data-testid="body-subtitle"
            tagName="div"
            typographyVariant={'body4'}
            width="100%"
            minWidth={0}
            textAlign="center"
            wordBreak="break-word"
          >
            {/* SSR/client must agree: `isRichTextContent` is DOM-only and returns false on the
                server, so wrap in NoSsr with the plain PrettyLink as fallback. SSR + first
                hydration emit the fallback; the rich render swaps in after mount.
                `sanitize`: notes migrated from the old plain textarea may start with HTML
                (e.g. `<img src=x onerror=…>`); sanitizing (inside the lazy widget chunk) keeps
                `text-align` and strips XSS. `textAlign="center"` keeps legacy notes centered once
                they render as rich text; explicit inline alignment still wins. */}
            <NoSsr fallback={plainSubTitle}>
              {isRichTextContent(subTitle) ? (
                <LazyRichText
                  richTextComponentData={{ id, text: subTitle }}
                  sanitize
                  textAlign="center"
                />
              ) : (
                plainSubTitle
              )}
            </NoSsr>
          </TextV2>
        </GuestSiteTypographyOverride>
      </Box>
    );
  }

  if (subTitle) {
    // Rich text is only enabled for the registry page (legacy + migrated). Other
    // pages still save the subtitle as a plain string from a textarea, so rendering
    // their values via dangerouslySetInnerHTML would be a stored-XSS surface.
    //
    // PageNote also renders as page chrome outside the NoSsr-wrapped PageWidget, so
    // SSR and client must agree. `isRichTextContent` is DOM-only and returns false
    // on the server — wrap the rich-text branch in NoSsr so SSR and the first
    // client render emit the plain PrettyLink, and the rich render swaps in after
    // mount.
    const isRegistry = isRegistryPage(type, pageSlug);
    const plainSubTitle = <PrettyLink source={page?.pageSlug}>{subTitle}</PrettyLink>;
    return (
      <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
        <TextV2
          data-testid="body-subtitle"
          tagName="div"
          typographyVariant={'body4'}
          marginTop={{ _: 7, sm2: 9 }}
          maxWidth={500}
          textAlign="center"
          wordBreak="break-word"
        >
          <InlineEditor
            actionData={{ edit: onInlineEditClick }}
            wrapperType="actionOutside"
            componentName="pageNote"
            elementLabel={tq`Page Note`}
            pageName={page?.type}
            pageSlug={pageSlug || ''}
            wrapperCSS={{ zIndex: 30 }}
            isEligibleForInlineEditing={isEligibleForInlineEditing}
          >
            {isRegistry ? (
              <NoSsr fallback={plainSubTitle}>{isRichTextContent(subTitle) ? <LazyRichText richTextComponentData={{ id, text: subTitle }} /> : plainSubTitle}</NoSsr>
            ) : (
              plainSubTitle
            )}
          </InlineEditor>
        </TextV2>
      </GuestSiteTypographyOverride>
    );
  }

  return null;
};

Body.displayName = 'Body';

export { Body };
